/**
 * CipIdPriorityPage — Compliance · CIP ID Priority (Person-Modal slot machine).
 *
 * A single GLOBAL drag-to-reorder list of acceptable CIP ID types (Bill
 * 2026-05-27: rank is global, not per-cohort — seeded F254-code ascending).
 * List order IS the rank (rank 1 = top). Each type has an active/inactive
 * toggle. This config drives Slot BL's `assignCipSlots`: the highest-ranked
 * ACTIVE captured photo-ID lands in CIP ID1, the next in ID2 — so a Passport
 * captured first takes ID1, then a higher-ranked Driver License bumps it to
 * ID2 (brief §2.5). Per-cohort behavior emerges for free: an NRA never captures
 * a DL, so the Passport is the highest captured ID and becomes ID1.
 *
 * Contract: GET /v3/config/cip-id-priority (Slot TR). Reorder + active writes
 * go to /v3/admin/cip-id-priority/{reorder,upsert} (documented contract C5).
 * If TR hasn't shipped the route to this branch, the page shows a non-fatal
 * "route pending" banner and still renders the Core Director Field 254 catalog
 * as a reference (the ID-type labels are read from /v3/config/core-director-codes,
 * NOT hard-coded — per the task brief).
 *
 * 5 admin-quality bars:
 *   wired         — reads /v3/config/cip-id-priority + the F254 catalog; writes reorder/active.
 *   configurable  — the global ranked priority is fully admin-driven.
 *   makes sense   — explains the slot machine inline; rank shown as #1/#2/… not raw ints.
 *   modern control— drag handle + reorder + toggle; NO free-text rank inputs.
 *   no-breaking   — cannot delete a type (deactivate only); cannot save with zero
 *                   active types; reorder requires a legacy citation.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { TextArea } from '@/components/FormField';
import {
  PersonModalConfigApi,
  errMessage,
  isRouteNotLive,
  type CipIdPriorityRule,
} from '@/services/personModalConfigApi';

/** Stable signature of the list's order + active flags — drives the dirty check. */
function listSignature(rows: ReadonlyArray<CipIdPriorityRule>): string {
  return rows.map((r) => `${r.idTypeCode}:${r.isActive ? 1 : 0}`).join('|');
}

function cloneRows(rows: ReadonlyArray<CipIdPriorityRule>): CipIdPriorityRule[] {
  return rows.map((r) => ({ ...r }));
}

export function CipIdPriorityPage(): JSX.Element {
  const client = useApiClient();
  const api = useMemo(() => new PersonModalConfigApi(client), [client]);

  const [serverRows, setServerRows] = useState<CipIdPriorityRule[] | null>(null);
  const [working, setWorking] = useState<CipIdPriorityRule[]>([]);
  const [catalog, setCatalog] = useState<Map<string, string>>(new Map());
  const [citation, setCitation] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [routePending, setRoutePending] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  // ── Load priority rows + the F254 catalog in parallel ──────────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      setServerRows(null);
      setLoadError(null);
      setRoutePending(false);

      // F254 ID-type catalog — labels come from the catalog, never hard-coded.
      try {
        const cat = await client.retail.config.coreDirectorCodes({ signal: ctrl.signal });
        if (!ctrl.signal.aborted && cat.success && Array.isArray(cat.data)) {
          const m = new Map<string, string>();
          for (const c of cat.data) {
            if (c.domain === 'IdVerifyCode' && c.isActive) m.set(c.code, c.label);
          }
          setCatalog(m);
        }
      } catch {
        /* catalog is best-effort; the page still works with code-only labels */
      }

      // Priority rows (single global list).
      try {
        const resp = await api.listCipIdPriority(ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (resp.success && Array.isArray(resp.data)) {
          const sorted = [...resp.data].sort((a, b) => a.rank - b.rank);
          setServerRows(sorted);
          setWorking(cloneRows(sorted));
          setCitation(sorted.find((r) => r.legacyProcedureCitation)?.legacyProcedureCitation ?? '');
        } else {
          setServerRows([]);
          setLoadError(resp.error ?? 'Failed to load CIP ID priority rows.');
        }
      } catch (e) {
        if (ctrl.signal.aborted) return;
        if (isRouteNotLive(e)) {
          setServerRows([]);
          setWorking([]);
          setRoutePending(true);
        } else {
          setServerRows([]);
          setLoadError(errMessage(e));
        }
      }
    })();
    return () => ctrl.abort();
  }, [api, client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const labelFor = useCallback((code: string) => catalog.get(code) ?? `Code ${code}`, [catalog]);

  const dirty = useMemo(
    () => listSignature(working) !== listSignature(serverRows ?? []),
    [working, serverRows],
  );

  const activeCount = working.filter((r) => r.isActive).length;

  // ── Drag reorder (native HTML5; no external lib) ───────────────────────────
  const onDrop = useCallback(
    (targetIndex: number) => {
      if (dragIndex === null || dragIndex === targetIndex) {
        setDragIndex(null);
        return;
      }
      setWorking((prev) => {
        const list = [...prev];
        const [moved] = list.splice(dragIndex, 1);
        list.splice(targetIndex, 0, moved);
        return list;
      });
      setDragIndex(null);
    },
    [dragIndex],
  );

  const toggleActive = useCallback((idTypeCode: string) => {
    setWorking((prev) =>
      prev.map((r) => (r.idTypeCode === idTypeCode ? { ...r, isActive: !r.isActive } : r)),
    );
  }, []);

  const resetList = useCallback(() => {
    if (!serverRows) return;
    setWorking(cloneRows(serverRows));
    setSaveError(null);
  }, [serverRows]);

  // ── Save: reorder + active flips, gated on a legacy citation ───────────────
  const handleSave = useCallback(async () => {
    if (citation.trim().length === 0) {
      setSaveError('A legacy-procedure citation is required to change CIP priority (cite-or-defer rule).');
      return;
    }
    if (activeCount === 0) {
      setSaveError('At least one ID type must stay active — no acceptable IDs would break CIP.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const server = serverRows ?? [];
      const serverActive = new Map(server.map((r) => [r.idTypeCode, r.isActive]));

      // 1) Order write (reorder = the full ranked list).
      const orderChanged =
        working.map((r) => r.idTypeCode).join(',') !== server.map((r) => r.idTypeCode).join(',');
      if (orderChanged) {
        await api.reorderCipIdPriority({
          idTypeCodes: working.map((r) => r.idTypeCode),
          legacyProcedureCitation: citation.trim(),
        });
      }

      // 2) Active flips (one upsert per changed row).
      for (const r of working) {
        if (serverActive.get(r.idTypeCode) !== r.isActive) {
          await api.upsertCipIdPriority({
            idTypeCode: r.idTypeCode,
            isActive: r.isActive,
            legacyProcedureCitation: citation.trim(),
          });
        }
      }

      setToast('Saved CIP ID priority.');
      refresh();
    } catch (e) {
      setSaveError(
        isRouteNotLive(e)
          ? 'Write route not live in this branch yet (Slot TR pending). The editor is wired; saving works once TR ships /v3/admin/cip-id-priority/*.'
          : errMessage(e),
      );
    } finally {
      setSaving(false);
    }
  }, [citation, activeCount, serverRows, working, api, refresh]);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <Banner tone="err">
        Could not load CIP ID priority: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!serverRows) return <Spinner label="Loading CIP ID priority…" />;

  return (
    <>
      {toast && (
        <Banner tone="info">
          {toast} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        Drag to set the single, bank-wide <strong>acceptable-ID priority order</strong>. The top
        active type wins <strong>CIP ID1</strong>; the next wins <strong>ID2</strong>. A higher-ranked
        ID captured later auto-bumps a lower one (Passport → ID1, then a Driver License bumps Passport
        to ID2). Per-cohort behavior emerges automatically — an NRA never captures a DL, so the
        Passport stays the highest captured ID. Toggle a type off to stop it satisfying a CIP slot —
        it is never deleted, so no captured ID is ever dropped. Labels come from the Core Director
        Field 254 catalog.
      </Banner>

      {routePending && (
        <Banner tone="warn">
          <strong>Seed pending.</strong> The <code>/v3/config/cip-id-priority</code> route isn’t
          live in this branch yet (Slot TR seeds it F254-code ascending with Bill’s legacy citation).
          The catalog below is shown for reference; reordering activates once the route ships.
        </Banner>
      )}

      {working.length === 0 ? (
        <Card title="Core Director Field 254 — acceptable ID types">
          <p className="page-intro">
            No priority rows seeded yet. These are the candidate ID types from the F254 catalog the
            slot machine will rank (default: code ascending):
          </p>
          <ul className="prio-list">
            {[...catalog.entries()]
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([code, label]) => (
                <li key={code} className="prio-row prio-row--static">
                  <span className="prio-code">{code}</span>
                  <span className="prio-label">{label}</span>
                </li>
              ))}
            {catalog.size === 0 && <li className="prio-row prio-row--static">Catalog unavailable.</li>}
          </ul>
        </Card>
      ) : (
        <Card
          title={
            <span>
              CIP ID Priority — global{' '}
              <Pill tone="info">{activeCount} active</Pill>{' '}
              {working.length - activeCount > 0 && (
                <Pill tone="muted">{working.length - activeCount} off</Pill>
              )}
              {dirty && <Pill tone="warn">unsaved</Pill>}
            </span>
          }
        >
          <ol className="prio-list" aria-label="CIP ID priority (global)">
            {working.map((r, idx) => {
              const activeRankBefore = working.slice(0, idx).filter((x) => x.isActive).length;
              const slot = r.isActive
                ? activeRankBefore === 0
                  ? 'ID1'
                  : activeRankBefore === 1
                    ? 'ID2'
                    : null
                : null;
              return (
                <li
                  key={r.idTypeCode}
                  className={`prio-row${dragIndex === idx ? ' prio-row--dragging' : ''}${
                    r.isActive ? '' : ' prio-row--inactive'
                  }`}
                  draggable
                  onDragStart={() => setDragIndex(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(idx)}
                  onDragEnd={() => setDragIndex(null)}
                >
                  <span className="prio-handle" aria-hidden="true" title="Drag to reorder">
                    ⠿
                  </span>
                  <span className="prio-rank">#{idx + 1}</span>
                  <span className="prio-code">{r.idTypeCode}</span>
                  <span className="prio-label">{labelFor(r.idTypeCode)}</span>
                  {slot && <Pill tone="ok">{slot}</Pill>}
                  <button
                    type="button"
                    className={`prio-toggle${r.isActive ? ' prio-toggle--on' : ''}`}
                    onClick={() => toggleActive(r.idTypeCode)}
                    aria-label={`${labelFor(r.idTypeCode)} is ${
                      r.isActive ? 'active' : 'inactive'
                    }; click to ${r.isActive ? 'deactivate' : 'activate'}`}
                  >
                    {r.isActive ? 'Active' : 'Inactive'}
                  </button>
                </li>
              );
            })}
          </ol>

          <div className="prio-save">
            <TextArea
              label="Legacy-procedure citation"
              value={citation}
              onChange={setCitation}
              rows={2}
              placeholder="PCB procedure memo / Legacy Procedures PDF page / Collette confirmation / Aurora finding."
              hint="Required to save. Cite-or-defer (CLAUDE.md rule #1) — priority order must trace to a documented source."
            />
            {saveError && <Banner tone="err">{saveError}</Banner>}
            {activeCount === 0 && (
              <Banner tone="warn">
                All ID types are inactive — CIP could never be satisfied. Re-activate at least one
                before saving.
              </Banner>
            )}
            <div className="prio-save__actions">
              <Button variant="ghost" onClick={resetList} disabled={saving || !dirty}>
                Reset
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving || !dirty}>
                {saving ? 'Saving…' : 'Save priority'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </>
  );
}
